/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
type RowWithId = {rowId?: string};

type GridRange = {
  startRow?: {rowIndex?: number};
  endRow?: {rowIndex?: number};
};

type GridRowNode = {
  data?: RowWithId;
  setSelected?: (selected: boolean) => void;
};

export type GridRowsReadApi<T extends RowWithId = RowWithId> = {
  forEachNode?: (callback: (node: {data?: T}) => void) => void;
};

export type GridSelectionApi = {
  getSelectedRows?: () => RowWithId[];
  getCellRanges?: () => GridRange[];
  getDisplayedRowAtIndex?: (rowIndex: number) => GridRowNode | undefined;
  deselectAll?: () => void;
};

/**
 * Resolves all row indexes covered by the current grid range selection.
 *
 * ```typescript
 * const rowIndexes = getRangeRowIndexesFromApi(api);
 * ```
 *
 * @param   api Grid selection API used to read cell ranges.
 * @returns A set of row indexes included in the selected ranges.
 */
export const getRangeRowIndexesFromApi = (api: GridSelectionApi): Set<number> => {
  const rowIndexes = new Set<number>();
  const ranges = api?.getCellRanges?.() || [];
  ranges.forEach((range) => {
    const startIndex = range?.startRow?.rowIndex;
    const endIndex = range?.endRow?.rowIndex;
    if (startIndex !== undefined && endIndex !== undefined) {
      const from = Math.min(startIndex, endIndex);
      const to = Math.max(startIndex, endIndex);
      for (let rowIndex = from; rowIndex <= to; rowIndex += 1) {
        rowIndexes.add(rowIndex);
      }
    }
  });

  return rowIndexes;
};

/**
 * Resolves selected row IDs from direct row selection and range selection.
 *
 * ```typescript
 * const selectedIds = getSelectedRowIdsFromApi(api);
 * ```
 *
 * @param   api Grid selection API used to read selected rows and ranges.
 * @returns A set of unique selected row IDs.
 */
export const getSelectedRowIdsFromApi = (api: GridSelectionApi): Set<string> => {
  const selectedByRow = api?.getSelectedRows?.() || [];
  const selectedByRowIds = new Set(selectedByRow.map((row) => row?.rowId).filter(Boolean) as string[]);

  const selectedByRangeIds = new Set<string>();
  const rowIndexes = getRangeRowIndexesFromApi(api);
  rowIndexes.forEach((rowIndex) => {
    const rowNode = api?.getDisplayedRowAtIndex?.(rowIndex);
    const rowId = rowNode?.data?.rowId;
    if (rowId) {
      selectedByRangeIds.add(rowId);
    }
  });

  const selectedIds = new Set<string>();
  selectedByRowIds.forEach((rowId) => {
    selectedIds.add(rowId);
  });
  selectedByRangeIds.forEach((rowId) => {
    selectedIds.add(rowId);
  });

  return selectedIds;
};

/**
 * Retrieves the total number of unique selected rows from the grid.
 *
 * ```typescript
 * const count = getSelectedRowCountFromApi(api);
 * ```
 *
 * @param   api Grid selection API used to resolve selected row IDs.
 * @returns The number of unique selected rows.
 */
export const getSelectedRowCountFromApi = (api: GridSelectionApi): number => {
  return getSelectedRowIdsFromApi(api).size;
};

/**
 * Returns selected row objects from the provided rows array based on current
 * grid selection (direct selection + range selection).
 *
 * ```typescript
 * const selectedRows = getRowsFromSelection(rows, api);
 * ```
 *
 * @param   rows Source rows to filter.
 * @param   api  Grid selection API used to resolve selected row IDs.
 * @returns The selected rows in their original order.
 */
export const getRowsFromSelection = <T extends {rowId: string}>(rows: T[], api: GridSelectionApi): T[] => {
  const selectedIds = getSelectedRowIdsFromApi(api);
  if (selectedIds.size) {
    return rows.filter((row) => selectedIds.has(row.rowId));
  }
  return [];
};

/**
 * Reads current displayed row data from AG Grid in render order.
 *
 * ```typescript
 * const rows = getGridRowsFromApi<RowType>(api);
 * ```
 *
 * @param   api Grid API exposing row iteration via `forEachNode`.
 * @returns Grid row data collected from all displayed nodes.
 */
export const getGridRowsFromApi = <T extends RowWithId>(api?: GridRowsReadApi<T>): T[] => {
  const rows: T[] = [];
  api?.forEachNode?.((node) => {
    if (node?.data) {
      rows.push(node.data);
    }
  });

  return rows;
};
